document.addEventListener('DOMContentLoaded', () => {
  const FILTER_MAKE = 'filter.p.m.custom.car_make';
  const FILTER_MODEL = 'filter.p.m.custom.car_model';
  const FILTER_YEAR = 'filter.p.m.custom.car_year';

  const YMM_VISIBLE_LIMIT = 6;

  let ymmItems = [];
  let updateTimer = null;
  let observerPaused = false;

  /*
   * Keeps the Show More / Show Less state for each filter.
   */
  const expandedState = new Map([
    [FILTER_MODEL, false],
    [FILTER_YEAR, false],
  ]);

  function clean(value) {
    return String(value ?? '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function unique(values) {
    return [...new Set(values.map(clean).filter(Boolean))];
  }

  function slugify(value) {
    return clean(value)
      .toLowerCase()
      .replace(/[^a-z0-9\u0590-\u05ff]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function escapeCss(value) {
    if (window.CSS?.escape) {
      return CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
  }

  function getInputs(paramName) {
    return [
      ...document.querySelectorAll(
        `input[type="checkbox"][name="${escapeCss(paramName)}"]`
      ),
    ];
  }

  function getSelectedValues(paramName) {
    return getInputs(paramName)
      .filter((input) => input.checked)
      .map((input) => clean(input.value));
  }

  function getRow(input) {
    if (!input) return null;

    return (
      input.closest('li.facet-options-list-item') ||
      input.closest('li') ||
      input.closest('label')
    );
  }

  function getFilterList(paramName) {
    const inputs = getInputs(paramName);

    if (!inputs.length) return null;

    return (
      getRow(inputs[0])?.closest('ul.facet-options-list') ||
      inputs[0].closest('ul')
    );
  }

  function getFacetContainer(paramName) {
    const inputs = getInputs(paramName);

    if (!inputs.length) return null;

    return (
      inputs[0].closest('details') ||
      inputs[0].closest('.facet') ||
      inputs[0].closest('.filter-group') ||
      inputs[0].closest('[class*="facet"]')
    );
  }

  /*
   * Returns the collection URL without active filters,
   * sorting, pagination or URL fragments.
   */
  function getCollectionUrl() {
    const url = new URL(window.location.href);

    url.search = '';
    url.hash = '';

    return url;
  }

  /*
   * Reads all Make / Model / Year values from the existing
   * collection ?view=ymm-json endpoint.
   */
  async function fetchAllYmmItems() {
    const collectionUrl = getCollectionUrl();
    const products = [];

    for (let page = 1; page <= 50; page += 1) {
      const endpoint = new URL(collectionUrl);

      endpoint.searchParams.set('view', 'ymm-json');
      endpoint.searchParams.set('page', String(page));

      let response;

      try {
        response = await fetch(endpoint.toString(), {
          credentials: 'same-origin',
        });
      } catch (error) {
        console.error('[YMM Grid] Fetch error:', error);
        break;
      }

      if (!response.ok) {
        console.error(
          '[YMM Grid] Request failed:',
          response.status,
          endpoint.toString()
        );
        break;
      }

      let data;

      try {
        data = await response.json();
      } catch (error) {
        console.error(
          '[YMM Grid] Invalid JSON:',
          endpoint.toString(),
          error
        );
        break;
      }

      if (Array.isArray(data.products)) {
        products.push(...data.products);
      }

      if (!data.has_more) {
        break;
      }
    }

    return products
      .filter((item) => item?.make && item?.model)
      .map((item) => {
        const years = Array.isArray(item.years)
          ? item.years
          : item.years
            ? [item.years]
            : [];

        return {
          make: clean(item.make),
          model: clean(item.model),
          years: unique(years),
        };
      });
  }

  function setGroupVisible(paramName, visible) {
    const group = getFacetContainer(paramName);

    if (!group) return;

    group.hidden = !visible;

    if (visible) {
      group.style.removeProperty('display');
    } else {
      group.style.setProperty('display', 'none', 'important');
    }
  }

  /*
   * Marks a value as relevant or irrelevant.
   * Show More limiting is applied separately afterward.
   */
  function forceRowState(input, relevant) {
    const row = getRow(input);

    if (!row) return;

    input.disabled = !relevant;

    if (relevant) {
      row.hidden = false;
      row.removeAttribute('hidden');
      row.classList.add('ymm-force-visible');
      row.classList.remove('ymm-force-hidden');
      row.style.setProperty('display', 'list-item', 'important');
    } else {
      row.hidden = true;
      row.setAttribute('hidden', '');
      row.classList.remove('ymm-force-visible');
      row.classList.add('ymm-force-hidden');
      row.style.setProperty('display', 'none', 'important');
    }
  }

  function clearInvalidSelections(paramName, validValues) {
    getInputs(paramName).forEach((input) => {
      const value = clean(input.value);

      if (input.checked && !validValues.has(value)) {
        input.checked = false;
      }
    });
  }

  function getValidModels(selectedMakes) {
    return new Set(
      ymmItems
        .filter((item) => selectedMakes.includes(item.make))
        .map((item) => item.model)
    );
  }

  function getValidYears(selectedMakes, selectedModels) {
    const years = new Set();

    ymmItems.forEach((item) => {
      if (
        selectedMakes.includes(item.make) &&
        selectedModels.includes(item.model)
      ) {
        item.years.forEach((year) => {
          years.add(clean(year));
        });
      }
    });

    return years;
  }

  /*
   * Adds Model or Year checkboxes that Shopify omitted
   * from the current filtered facet HTML.
   */
  function ensureFilterValues(paramName, values) {
    const existingInputs = getInputs(paramName);

    if (!existingInputs.length) {
      console.warn(
        `[YMM Grid] Cannot generate values because no template input exists for ${paramName}.`
      );
      return;
    }

    const templateInput = existingInputs[0];
    const templateRow = getRow(templateInput);
    const list = getFilterList(paramName);

    if (!templateRow || !list) return;

    const existingValues = new Set(
      existingInputs.map((input) => clean(input.value))
    );

    const selectedUrlValues = new Set(
      new URL(window.location.href).searchParams
        .getAll(paramName)
        .map(clean)
    );

    values.forEach((rawValue) => {
      const value = clean(rawValue);

      if (!value || existingValues.has(value)) {
        return;
      }

      const newRow = templateRow.cloneNode(true);
      const newInput = newRow.querySelector(
        'input[type="checkbox"]'
      );
      const newLabel = newRow.querySelector('label');
      const textElement = newRow.querySelector(
        '.checkbox-facet-label-text'
      );
      const countElement = newRow.querySelector(
        '.checkbox-facet-label-count'
      );

      if (!newInput) return;

      const uniqueId =
        `Ymm-${slugify(paramName)}-${slugify(value)}`;

      newInput.id = uniqueId;
      newInput.name = paramName;
      newInput.value = value;
      newInput.checked = selectedUrlValues.has(value);
      newInput.disabled = false;
      newInput.dataset.ymmGenerated = 'true';

      newInput.removeAttribute('disabled');
      newInput.removeAttribute('aria-disabled');

      if (newInput.checked) {
        newInput.setAttribute('checked', '');
      } else {
        newInput.removeAttribute('checked');
      }

      if (templateInput.hasAttribute('form')) {
        newInput.setAttribute(
          'form',
          templateInput.getAttribute('form')
        );
      }

      if (newLabel) {
        newLabel.setAttribute('for', uniqueId);
      }

      if (textElement) {
        textElement.textContent = value;
      }

      /*
       * Shopify did not provide a facet count for a generated value,
       * so do not show a copied and incorrect number.
       */
      if (countElement) {
        countElement.textContent = '';
        countElement.hidden = true;
        countElement.style.setProperty('display', 'none', 'important');
      }

      newRow.hidden = false;
      newRow.removeAttribute('hidden');
      newRow.style.removeProperty('display');

      newRow.classList.remove(
        'ymm-force-hidden',
        'ymm-force-visible'
      );

      list.appendChild(newRow);
      existingValues.add(value);
    });
  }

  function sortFilterRows(paramName, compareFunction) {
    const inputs = getInputs(paramName);
    const list = getFilterList(paramName);

    if (!inputs.length || !list) return;

    const rows = inputs
      .map((input) => ({
        row: getRow(input),
        value: clean(input.value),
      }))
      .filter((item) => item.row);

    rows.sort((a, b) =>
      compareFunction(a.value, b.value)
    );

    rows.forEach(({ row }) => {
      list.appendChild(row);
    });
  }

  function sortModelsAlphabetically() {
    sortFilterRows(
      FILTER_MODEL,
      (a, b) =>
        a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
    );
  }

  function sortYearsDescending() {
    sortFilterRows(
      FILTER_YEAR,
      (a, b) => {
        const yearA = Number.parseInt(a, 10) || 0;
        const yearB = Number.parseInt(b, 10) || 0;

        return yearB - yearA;
      }
    );
  }

  function getShowMoreControls(paramName) {
    const facet = getFacetContainer(paramName);

    if (!facet) {
      return {
        showMore: null,
        showLess: null,
      };
    }

    const controls = [
      ...facet.querySelectorAll(
        'button, a, [data-show-more], [data-show-less], [class*="show-more"], [class*="show-less"]'
      ),
    ];

    const showMore =
      controls.find((element) => {
        const text = clean(element.textContent).toLowerCase();

        return text.includes('show more');
      }) || null;

    const showLess =
      controls.find((element) => {
        const text = clean(element.textContent).toLowerCase();

        return text.includes('show less');
      }) || null;

    return {
      showMore,
      showLess,
    };
  }

  function isRelevantInput(input) {
    const row = getRow(input);

    return Boolean(
      row &&
      row.classList.contains('ymm-force-visible') &&
      !row.classList.contains('ymm-force-hidden')
    );
  }

  /*
   * Shows the first six relevant values, or all relevant values
   * when expanded.
   */
  function applyYmmListLimit(paramName) {
    const expanded = expandedState.get(paramName) === true;

    const relevantInputs = getInputs(paramName)
      .filter(isRelevantInput);

    relevantInputs.forEach((input, index) => {
      const row = getRow(input);

      if (!row) return;

      const shouldDisplay =
        expanded ||
        input.checked ||
        index < YMM_VISIBLE_LIMIT;

      row.hidden = !shouldDisplay;

      if (shouldDisplay) {
        row.removeAttribute('hidden');
        row.style.setProperty(
          'display',
          'list-item',
          'important'
        );
      } else {
        row.setAttribute('hidden', '');
        row.style.setProperty(
          'display',
          'none',
          'important'
        );
      }
    });

    const { showMore, showLess } =
      getShowMoreControls(paramName);

    const needsControls =
      relevantInputs.length > YMM_VISIBLE_LIMIT;

    if (showMore) {
      showMore.dataset.ymmParamName = paramName;
      showMore.dataset.ymmAction = 'expand';

      if (needsControls && !expanded) {
        showMore.style.removeProperty('display');
      } else {
        showMore.style.setProperty(
          'display',
          'none',
          'important'
        );
      }
    }

    if (showLess) {
      showLess.dataset.ymmParamName = paramName;
      showLess.dataset.ymmAction = 'collapse';

      if (needsControls && expanded) {
        showLess.style.removeProperty('display');
      } else {
        showLess.style.setProperty(
          'display',
          'none',
          'important'
        );
      }
    }
  }

  /*
   * Applies a generated checkbox through the collection URL.
   * Native Shopify checkboxes continue using the Theme's AJAX.
   */
  function applyGeneratedFilterToUrl(paramName) {
    const url = new URL(window.location.href);

    url.searchParams.delete(paramName);

    getInputs(paramName)
      .filter((input) => input.checked)
      .forEach((input) => {
        url.searchParams.append(
          paramName,
          clean(input.value)
        );
      });

    url.searchParams.delete('page');

    window.location.assign(url.toString());
  }

  function updateDependentFilters() {
    if (!ymmItems.length) return;

    observerPaused = true;

    try {
      const makeInputs = getInputs(FILTER_MAKE);

      if (!makeInputs.length) return;

      const selectedMakes =
        getSelectedValues(FILTER_MAKE);

      /*
       * MODEL depends on MAKE.
       */
      if (!selectedMakes.length) {
        setGroupVisible(FILTER_MODEL, false);
        setGroupVisible(FILTER_YEAR, false);
        return;
      }

      const validModels =
        getValidModels(selectedMakes);

      ensureFilterValues(
        FILTER_MODEL,
        [...validModels]
      );

      clearInvalidSelections(
        FILTER_MODEL,
        validModels
      );

      setGroupVisible(FILTER_MODEL, true);

      getInputs(FILTER_MODEL).forEach((input) => {
        forceRowState(
          input,
          validModels.has(clean(input.value))
        );
      });

      sortModelsAlphabetically();
      applyYmmListLimit(FILTER_MODEL);

      const selectedModels =
        getSelectedValues(FILTER_MODEL);

      /*
       * YEAR depends on MAKE + MODEL.
       */
      if (!selectedModels.length) {
        setGroupVisible(FILTER_YEAR, false);
        return;
      }

      const validYears = getValidYears(
        selectedMakes,
        selectedModels
      );

      ensureFilterValues(
        FILTER_YEAR,
        [...validYears]
      );

      clearInvalidSelections(
        FILTER_YEAR,
        validYears
      );

      setGroupVisible(FILTER_YEAR, true);

      getInputs(FILTER_YEAR).forEach((input) => {
        forceRowState(
          input,
          validYears.has(clean(input.value))
        );
      });

      sortYearsDescending();
      applyYmmListLimit(FILTER_YEAR);

      console.log('[YMM Grid]', {
        selectedMakes,
        validModels: [...validModels],
        selectedModels,
        validYears: [...validYears],
      });
    } finally {
      window.setTimeout(() => {
        observerPaused = false;
      }, 150);
    }
  }

  function scheduleUpdate(delay = 100) {
    window.clearTimeout(updateTimer);

    updateTimer = window.setTimeout(() => {
      updateDependentFilters();
    }, delay);
  }

  /*
   * YMM checkbox changes.
   */
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;

      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      if (
        input.name !== FILTER_MAKE &&
        input.name !== FILTER_MODEL &&
        input.name !== FILTER_YEAR
      ) {
        return;
      }

      /*
       * Generated values did not exist when the Theme initialized
       * its native facet listeners. Apply them directly by URL.
       */
      if (input.dataset.ymmGenerated === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();

        applyGeneratedFilterToUrl(input.name);
        return;
      }

      /*
       * Native Shopify value: allow the Theme to handle it,
       * then update the dependent values.
       */
      scheduleUpdate(50);
    },
    true
  );

  /*
   * Custom Show More / Show Less handling.
   */
  document.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest(
        '[data-ymm-action]'
      );

      if (!control) return;

      const paramName =
        control.dataset.ymmParamName;

      const action =
        control.dataset.ymmAction;

      if (!paramName || !action) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      expandedState.set(
        paramName,
        action === 'expand'
      );

      applyYmmListLimit(paramName);
    },
    true
  );

  async function initialize() {
    ymmItems = await fetchAllYmmItems();

    if (!ymmItems.length) {
      console.warn(
        '[YMM Grid] No YMM records found for this collection.'
      );
      return;
    }

    updateDependentFilters();

    /*
     * The Theme replaces filters after AJAX requests.
     * Rebuild and reapply YMM dependencies afterward.
     */
    const observer = new MutationObserver((mutations) => {
      if (observerPaused) return;

      const filterChanged = mutations.some((mutation) =>
        [...mutation.addedNodes].some((node) => {
          if (!(node instanceof HTMLElement)) {
            return false;
          }

          return (
            node.matches?.(
              '.facet, .facets, .facets-container, li.facet-options-list-item'
            ) ||
            node.querySelector?.(
              [
                `input[name="${FILTER_MAKE}"]`,
                `input[name="${FILTER_MODEL}"]`,
                `input[name="${FILTER_YEAR}"]`,
              ].join(',')
            )
          );
        })
      );

      if (filterChanged) {
        scheduleUpdate(150);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    console.log(
      `[YMM Grid] Loaded ${ymmItems.length} vehicle records.`
    );
  }

  initialize();
});